import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFirestoreDb } from '@/services/firebase';
import { ITEMS_COLLECTION } from '@/modules/inventory/services/constants';
import { getItem, saveItem } from '@/modules/inventory/services/itemRepository';
import type { InventoryItemInput, InventoryStatus } from '@/modules/inventory/types';
import { TRANSIT_LOCATION_SENTINEL } from '@/services/inventoryMovements/constants';
import type { InventoryMovementType } from '@/services/inventoryMovements/types';

export type ItemTrackingPatch = {
  currentLocationId?: string | null;
  lastMovementAt?: string;
  lastInventoryCheckAt?: string;
  status?: InventoryStatus;
  stockQuantity?: number;
};

export async function patchItemTracking(
  itemId: string,
  patch: ItemTrackingPatch,
): Promise<void> {
  const ref = doc(getFirestoreDb(), ITEMS_COLLECTION, itemId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data() as Record<string, unknown>;
  const updates: Record<string, unknown> = { updatedAt: new Date() };
  if (patch.currentLocationId !== undefined) {
    updates.currentLocationId = patch.currentLocationId;
  }
  if (patch.lastMovementAt !== undefined) {
    updates.lastMovementAt = patch.lastMovementAt;
  }
  if (patch.lastInventoryCheckAt !== undefined) {
    updates.lastInventoryCheckAt = patch.lastInventoryCheckAt;
  }
  if (patch.status !== undefined) {
    updates.status = patch.status;
  }
  if (patch.stockQuantity !== undefined && data.trackStock === true) {
    updates.stockQuantity = patch.stockQuantity;
  }

  await setDoc(ref, updates, { merge: true });
}

export function resolveTrackingAfterMovement(
  type: InventoryMovementType,
  params: {
    fromLocationId: string | null;
    toLocationId: string | null;
    currentStatus: InventoryStatus;
    quantity?: number | null;
    currentStock?: number;
  },
): ItemTrackingPatch {
  const now = new Date().toISOString();
  const base: ItemTrackingPatch = { lastMovementAt: now };

  switch (type) {
    case 'check_out':
      return {
        ...base,
        currentLocationId: TRANSIT_LOCATION_SENTINEL,
        status: params.currentStatus === 'sold' ? 'sold' : 'in_use',
      };
    case 'check_in':
      return {
        ...base,
        currentLocationId: params.toLocationId,
        status:
          params.currentStatus === 'maintenance' ||
          params.currentStatus === 'repair' ||
          params.currentStatus === 'inspection'
            ? params.currentStatus
            : 'available',
      };
    case 'transfer':
      return {
        ...base,
        currentLocationId: params.toLocationId,
      };
    case 'maintenance_out':
    case 'repair_out':
      return {
        ...base,
        currentLocationId: TRANSIT_LOCATION_SENTINEL,
      };
    case 'maintenance_in':
    case 'repair_in':
      return {
        ...base,
        currentLocationId: params.toLocationId ?? params.fromLocationId,
      };
    case 'inventory_count':
      return {
        ...base,
        lastInventoryCheckAt: now.slice(0, 10),
        ...(params.toLocationId !== null
          ? { currentLocationId: params.toLocationId }
          : {}),
      };
    case 'manual_adjustment':
      return {
        ...base,
        ...(params.quantity != null && params.currentStock != null
          ? { stockQuantity: params.currentStock + params.quantity }
          : {}),
      };
    default:
      return base;
  }
}

export async function applyItemTrackingFromMovement(
  itemId: string,
  type: InventoryMovementType,
  locations: { from: string | null; to: string | null },
  quantity?: number | null,
): Promise<void> {
  const item = await getItem(itemId);
  if (!item) throw new Error('Artículo no encontrado.');

  const patch = resolveTrackingAfterMovement(type, {
    fromLocationId: locations.from,
    toLocationId: locations.to,
    currentStatus: item.status,
    quantity: quantity ?? null,
    currentStock: item.stockQuantity,
  });

  await patchItemTracking(itemId, patch);

  if (patch.status && patch.status !== item.status) {
    const input: InventoryItemInput = {
      sku: item.sku,
      name: item.name,
      description: item.description,
      categoryId: item.categoryId,
      subcategoryId: item.subcategoryId,
      mode: item.mode,
      status: patch.status,
      customFieldValues: item.customFieldValues,
      trackStock: item.trackStock,
      stockQuantity: patch.stockQuantity ?? item.stockQuantity,
      stockUnit: item.stockUnit,
    };
    await saveItem(input, itemId);
  } else if (
    patch.stockQuantity !== undefined &&
    patch.stockQuantity !== item.stockQuantity
  ) {
    await saveItem(
      {
        sku: item.sku,
        name: item.name,
        description: item.description,
        categoryId: item.categoryId,
        subcategoryId: item.subcategoryId,
        mode: item.mode,
        status: item.status,
        customFieldValues: item.customFieldValues,
        trackStock: item.trackStock,
        stockQuantity: patch.stockQuantity,
        stockUnit: item.stockUnit,
      },
      itemId,
    );
  }
}

export function isItemPhysicallyOut(
  movements: { type: InventoryMovementType; createdAt: Date }[],
): boolean {
  if (movements.length === 0) return false;
  const sorted = [...movements].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );
  const last = sorted[0]!.type;
  return (
    last === 'check_out' ||
    last === 'maintenance_out' ||
    last === 'repair_out'
  );
}
